/**
 * `SettingsView` — ADR-0185 §2.4: platform settings the founder tunes
 * live, starting with the signup-breaker threshold.
 *
 * `signup_hourly_limit` is the max new accounts per rolling hour before
 * the /welcome confirm step demands a Turnstile pass (ADR-0185 §2.3).
 * Raise it ahead of expected signup bursts (press, launches); lower it
 * to 0 during an active flood to challenge every new account. Writes
 * are superadmin-only and staff-audited, same as every other
 * admin_setting mutation.
 *
 * The change log below the setting is the `setting_update` slice of the
 * permanent staff-audit trail — server-paginated and sortable (founder
 * feedback 2026-07-13: the table will grow a lot). The destructive bool
 * toggles (retention auto, deletion auto) keep living on their own
 * boards (Purge / Deletions) next to the runs they govern.
 */

import { ArrowDown, ArrowUp, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type {
  AdminSetting,
  AdminSettingChange,
  AdminAcpProbeResult,
  AdminAcpProbeVerdict,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

/** Mirrors the API's ADMIN_SETTING_INT_MIN/MAX bounds. */
const LIMIT_MIN = 0;
const LIMIT_MAX = 100000;
const PAGE_SIZE = 20;

interface IntSettingConfig {
  readonly key: 'signup_hourly_limit' | 'scan_hourly_limit' | 'report_email_hourly_limit';
  readonly kicker: string;
  readonly unit: string;
  readonly body: string;
  readonly note: string;
  readonly fallback: string;
}

const INT_SETTINGS: readonly IntSettingConfig[] = [
  {
    key: 'signup_hourly_limit',
    kicker: 'SIGNUP RATE LIMIT',
    unit: 'new accounts / hour',
    body:
      'Maximum new accounts per rolling hour before the signup confirm step '
      + 'requires a human-verification challenge (ADR-0185). Raise it ahead of '
      + 'a press release or launch; set 0 to challenge every new account '
      + 'during an active flood.',
    note:
      'Takes effect immediately. If Turnstile keys are not deployed, '
      + 'exceeding the limit pauses new signups instead of challenging them.',
    fallback: 'compiled-in default (10/hour)',
  },
  {
    key: 'scan_hourly_limit',
    kicker: 'SCAN RATE LIMIT',
    unit: 'domain scans / hour',
    body:
      'Maximum pre-account domain scans per rolling hour (ADR-0186). Each '
      + 'scan is one paid LLM call (~1.3 cents); this cap is the platform-wide '
      + 'wallet bound for the sill.so email-capture trigger. Raise it ahead '
      + 'of a launch; set 0 to pause pre-account scans entirely.',
    note:
      'Takes effect immediately. Scans are also deduplicated per domain for '
      + '7 days, so repeat checks of the same store never spend a slot.',
    fallback: 'compiled-in default (100/hour)',
  },
  {
    key: 'report_email_hourly_limit',
    kicker: 'REPORT EMAIL RATE LIMIT',
    unit: 'report emails / hour',
    body:
      'Maximum agent-readiness report emails per rolling hour (ADR-0199). '
      + 'Each report is one SES send plus the readiness probes; this cap is '
      + 'the platform-wide wallet bound for the signup report trigger. Set 0 '
      + 'to pause report emails entirely.',
    note:
      'Takes effect immediately. Reports are also deduplicated per '
      + '(email, domain) for 24 hours, so repeat sign-ins never spend a slot.',
    fallback: 'compiled-in default (200/hour)',
  },
];

type HistorySort = 'at' | 'key' | 'staff';
type HistoryDir = 'asc' | 'desc';

interface BoolSettingConfig {
  readonly key: 'agency_self_signup_enabled';
  readonly kicker: string;
  readonly on_label: string;
  readonly off_label: string;
  readonly body: string;
  readonly note: string;
  readonly fallback: string;
}

const BOOL_SETTINGS: readonly BoolSettingConfig[] = [
  {
    key: 'agency_self_signup_enabled',
    kicker: 'AGENCY SELF-SIGNUP',
    on_label: 'On — public pricing links agency tiers to the dashboard sign-up.',
    off_label: 'Off — public pricing keeps agency tiers as "talk to us".',
    body:
      'Controls whether the agency tier CTAs on sill.so/pricing link into '
      + 'app.sill.so/sign-up?plan=<agency-slug> and whether the dashboard '
      + 'posts a request to /v1/account/agency-request. A request grants '
      + 'NOTHING by itself — approval stays a staff action (Agency Feature '
      + 'card + Account → Plans assignment).',
    note:
      'Takes effect inside a 60-second CDN cache window on the public page. '
      + 'Flipping Off refuses new requests immediately; existing pending rows '
      + 'stay dismissible from the Agencies panel.',
    fallback: 'compiled-in default (off)',
  },
];

export function SettingsView(): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [settings, setSettings] = useState<readonly AdminSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [history, setHistory] = useState<readonly AdminSettingChange[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [sort, setSort] = useState<HistorySort>('at');
  const [dir, setDir] = useState<HistoryDir>('desc');

  const loadSetting = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.listSettings();
      setSettings(res.settings);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const loadHistory = useCallback(async (): Promise<void> => {
    setHistoryLoading(true);
    try {
      const res = await api.getSettingsHistory({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        sort,
        dir,
      });
      setHistory(res.entries);
      setHistoryTotal(res.total);
    } catch {
      // Non-fatal — the setting editor is the primary surface; the log
      // pane just shows empty on a read failure.
      setHistory([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  }, [api, page, sort, dir]);

  useEffect(() => {
    void loadSetting();
  }, [loadSetting]);
  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  const onSaved = useCallback((next: readonly AdminSetting[]): void => {
    setSettings(next);
    // The save just wrote an audit row — jump back to page 1 of the
    // log so the new entry is visible.
    setPage(0);
    void loadHistory();
  }, [loadHistory]);

  const toggleSort = (col: HistorySort): void => {
    if (sort === col) {
      setDir(dir === 'desc' ? 'asc' : 'desc');
    } else {
      setSort(col);
      setDir(col === 'at' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const pageCount = Math.max(1, Math.ceil(historyTotal / PAGE_SIZE));
  const rangeStart = historyTotal === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, historyTotal);

  const sortHeader = (col: HistorySort, label: string, align: 'left' | 'right' = 'left'): JSX.Element => (
    <th style={{ textAlign: align, padding: '0 0 10px' }}>
      <button
        type="button"
        onClick={() => toggleSort(col)}
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          letterSpacing: 1.5,
          color: sort === col ? C.text : C.muted,
          textTransform: 'uppercase',
          background: 'transparent',
          border: 0,
          padding: 0,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 4,
        }}
        aria-label={`Sort by ${label}`}
      >
        {label}
        {sort === col ? (dir === 'asc' ? <ArrowUp size={11} /> : <ArrowDown size={11} />) : null}
      </button>
    </th>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
      <div className="flex items-center justify-between" style={{ marginBottom: 32 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, marginBottom: 6 }}>
            PLATFORM SETTINGS
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 36, lineHeight: 1, color: C.text, letterSpacing: -1 }}>
            Settings
          </h1>
        </div>
        <button
          type="button"
          onClick={() => {
            void loadSetting();
            void loadHistory();
          }}
          className="flex items-center gap-2 px-3 py-2"
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.muted,
            background: C.surface,
            border: `1px solid ${C.border}`,
            borderRadius: 2,
          }}
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {error !== null ? (
        <Card style={{ padding: 24 }}>
          <p style={{ fontFamily: sansFont, fontSize: 14, color: C.coralFg, margin: 0 }}>{error}</p>
        </Card>
      ) : loading ? (
        <Card style={{ padding: 24 }}>
          <p style={{ fontFamily: sansFont, fontSize: 14, color: C.muted, margin: 0 }}>Loading…</p>
        </Card>
      ) : (
        <div style={{ display: 'grid', gap: 24 }}>
          {INT_SETTINGS.map((config) => (
            <IntSettingCard
              key={config.key}
              config={config}
              setting={settings.find((row) => row.key === config.key) ?? null}
              onSaved={onSaved}
            />
          ))}
          {BOOL_SETTINGS.map((config) => (
            <BoolSettingCard
              key={config.key}
              config={config}
              setting={settings.find((row) => row.key === config.key) ?? null}
              onSaved={onSaved}
            />
          ))}
        </div>
      )}

      <AcpProbeCard />

      <Card style={{ padding: 24, marginTop: 24 }}>
        <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.muted, marginBottom: 16 }}>
          CHANGE LOG
        </div>
        {historyLoading ? (
          <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, margin: 0 }}>Loading…</p>
        ) : historyTotal === 0 ? (
          <p style={{ fontFamily: sansFont, fontSize: 13, color: C.muted, margin: 0 }}>
            No settings changes recorded yet.
          </p>
        ) : (
          <>
            <TableScroll minWidth={520}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: `1px solid ${C.border}` }}>
                    {sortHeader('at', 'When')}
                    {sortHeader('key', 'Change')}
                    {sortHeader('staff', 'By', 'right')}
                  </tr>
                </thead>
                <tbody>
                  {history.map((h, i) => (
                    <tr key={`${h.at}-${i}`}>
                      <td
                        style={{
                          fontFamily: monoFont,
                          fontSize: 11,
                          color: C.muted,
                          padding: '10px 24px 10px 0',
                          whiteSpace: 'nowrap',
                          width: 1,
                          borderBottom: `1px solid ${C.borderSoft ?? C.border}`,
                        }}
                      >
                        {formatIsoCompact(h.at)}
                      </td>
                      <td
                        style={{
                          fontFamily: sansFont,
                          fontSize: 13,
                          color: C.text,
                          padding: '10px 24px 10px 0',
                          borderBottom: `1px solid ${C.borderSoft ?? C.border}`,
                        }}
                      >
                        <span style={{ fontFamily: monoFont, fontSize: 12 }}>{h.key}</span>
                        <span style={{ color: C.muted }}> → </span>
                        <span style={{ fontFamily: monoFont, fontSize: 12, color: C.limeFg }}>{h.value}</span>
                      </td>
                      <td
                        style={{
                          fontFamily: sansFont,
                          fontSize: 12,
                          color: C.muted,
                          padding: '10px 0',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          width: 1,
                          borderBottom: `1px solid ${C.borderSoft ?? C.border}`,
                        }}
                      >
                        {h.staff_name ?? h.staff_id}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
            <div className="flex items-center justify-between" style={{ marginTop: 16 }}>
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                {rangeStart}–{rangeEnd} of {historyTotal}
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage(Math.max(0, page - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: page === 0 ? C.muted : C.text,
                    background: C.surface2 ?? C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    opacity: page === 0 ? 0.5 : 1,
                    cursor: page === 0 ? 'default' : 'pointer',
                  }}
                >
                  Prev
                </button>
                <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
                  {page + 1}/{pageCount}
                </span>
                <button
                  type="button"
                  onClick={() => setPage(Math.min(pageCount - 1, page + 1))}
                  disabled={page >= pageCount - 1}
                  className="px-3 py-1.5"
                  style={{
                    fontFamily: sansFont,
                    fontSize: 12,
                    color: page >= pageCount - 1 ? C.muted : C.text,
                    background: C.surface2 ?? C.surface,
                    border: `1px solid ${C.border}`,
                    borderRadius: 2,
                    opacity: page >= pageCount - 1 ? 0.5 : 1,
                    cursor: page >= pageCount - 1 ? 'default' : 'pointer',
                  }}
                >
                  Next
                </button>
              </div>
            </div>
            <p style={{ fontFamily: sansFont, fontSize: 11, color: C.muted, marginTop: 14, marginBottom: 0 }}>
              Every change is stored permanently in the staff audit log.
            </p>
          </>
        )}
      </Card>
    </div>
  );
}

interface IntSettingCardProps {
  readonly config: IntSettingConfig;
  readonly setting: AdminSetting | null;
  readonly onSaved: (settings: readonly AdminSetting[]) => void;
}

function IntSettingCard({ config, setting, onSaved }: IntSettingCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [draft, setDraft] = useState(
    setting !== null && setting.int_value !== null ? String(setting.int_value) : '',
  );
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const parsed = Number(draft);
  const valid =
    draft.trim() !== ''
    && Number.isInteger(parsed)
    && parsed >= LIMIT_MIN
    && parsed <= LIMIT_MAX;
  const dirty = setting !== null && valid && parsed !== setting.int_value;

  const save = async (): Promise<void> => {
    if (!valid || saving) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const res = await api.setSetting(config.key, parsed);
      const row = res.settings.find((s) => s.key === config.key) ?? null;
      if (row !== null && row.int_value !== null) setDraft(String(row.int_value));
      setSaved(true);
      onSaved(res.settings);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'forbidden') {
        setSaveError('Your staff account is not authorized (superadmin required).');
      } else {
        setSaveError('Save failed. The setting is unchanged; retry or check logs.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 24 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.limeFg, marginBottom: 12 }}>
        {config.kicker}
      </div>
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: '0 0 8px' }}>
        {config.body}
      </p>
      <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
        {config.note}
      </p>
      {setting === null ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.amberFg ?? C.muted, margin: 0 }}>
          The {config.key} row is missing — the API is using its {config.fallback}.
          Run the latest migration, then refresh.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <input
              type="number"
              min={LIMIT_MIN}
              max={LIMIT_MAX}
              step={1}
              value={draft}
              onChange={(e) => {
                setDraft(e.target.value);
                setSaved(false);
              }}
              aria-label={config.unit}
              className="px-3 py-2 outline-none"
              style={{
                fontFamily: monoFont,
                fontSize: 14,
                width: 120,
                color: C.text,
                background: C.surface2 ?? C.surface,
                border: `1px solid ${valid || draft === '' ? C.border : C.coralFg}`,
                borderRadius: 2,
              }}
            />
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
              {config.unit}
            </span>
            <button
              type="button"
              onClick={() => void save()}
              disabled={!dirty || saving}
              className="px-4 py-2"
              style={{
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                background: dirty ? C.lime : C.surface2 ?? C.surface,
                color: dirty ? C.ink : C.muted,
                borderRadius: 2,
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
            {saved ? (
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.limeFg, letterSpacing: 1 }}>
                SAVED
              </span>
            ) : null}
          </div>
          {!valid && draft.trim() !== '' ? (
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 10, marginBottom: 0 }}>
              Enter a whole number between {LIMIT_MIN} and {LIMIT_MAX}.
            </p>
          ) : null}
          {saveError !== null ? (
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 10, marginBottom: 0 }}>
              {saveError}
            </p>
          ) : null}
          <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 20, marginBottom: 0 }}>
            Last changed {formatIsoCompact(setting.updated_at)}
            {setting.updated_by !== null
              ? ` by ${setting.updated_by_name ?? setting.updated_by}`
              : ' (seeded default)'}
          </p>
        </>
      )}
    </Card>
  );
}

interface BoolSettingCardProps {
  readonly config: BoolSettingConfig;
  readonly setting: AdminSetting | null;
  readonly onSaved: (settings: readonly AdminSetting[]) => void;
}

function BoolSettingCard({ config, setting, onSaved }: BoolSettingCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const current = setting?.bool_value ?? false;
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const apply = async (next: boolean): Promise<void> => {
    if (saving) return;
    setSaving(true);
    setSaveError(null);
    setSaved(false);
    try {
      const res = await api.setSetting(config.key, next);
      setSaved(true);
      onSaved(res.settings);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'forbidden') {
        setSaveError('Your staff account is not authorized (superadmin required).');
      } else {
        setSaveError('Save failed. The setting is unchanged; retry or check logs.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card style={{ padding: 24 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.limeFg, marginBottom: 12 }}>
        {config.kicker}
      </div>
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: '0 0 8px' }}>
        {config.body}
      </p>
      <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
        {config.note}
      </p>
      {setting === null ? (
        <p style={{ fontFamily: sansFont, fontSize: 13, color: C.amberFg ?? C.muted, margin: 0 }}>
          The {config.key} row is missing — the API is using its {config.fallback}.
          Run the latest migration, then refresh.
        </p>
      ) : (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void apply(!current)}
              disabled={saving}
              className="px-4 py-2"
              style={{
                fontFamily: sansFont,
                fontWeight: 600,
                fontSize: 13,
                background: current ? C.lime : C.surface2 ?? C.surface,
                color: current ? C.ink : C.text,
                border: `1px solid ${current ? C.lime : C.border}`,
                borderRadius: 2,
                opacity: saving ? 0.6 : 1,
              }}
              aria-pressed={current}
            >
              {saving ? 'Saving…' : current ? 'On' : 'Off'}
            </button>
            <span style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>
              {current ? config.on_label : config.off_label}
            </span>
            {saved ? (
              <span style={{ fontFamily: monoFont, fontSize: 11, color: C.limeFg, letterSpacing: 1 }}>
                SAVED
              </span>
            ) : null}
          </div>
          {saveError !== null ? (
            <p style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 10, marginBottom: 0 }}>
              {saveError}
            </p>
          ) : null}
          <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 20, marginBottom: 0 }}>
            Last changed {formatIsoCompact(setting.updated_at)}
            {setting.updated_by !== null
              ? ` by ${setting.updated_by_name ?? setting.updated_by}`
              : ' (seeded default)'}
          </p>
        </>
      )}
    </Card>
  );
}

/**
 * ADR-0130 slice 0 — the ops2026 ACP↔Stripe-Connect probe button.
 * Superadmin-only, TEST-mode (the server refuses any non-test Stripe
 * key). One click mints a test Shared Payment Token and tries to redeem
 * it under a Connect connected-account header, answering the one gating
 * question for the ACP settlement design: GO / NO-GO.
 */
function AcpProbeCard(): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<AdminAcpProbeResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async (): Promise<void> => {
    if (running) return;
    setRunning(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.runAcpProbe();
      setResult(res);
    } catch (err) {
      if (err instanceof ApiError && err.kind === 'forbidden') {
        setError('Your staff account is not authorized (superadmin required).');
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Rate limited — the probe is capped at 6 runs/hour. Try again later.');
      } else {
        setError('The probe could not run. Check the API logs and retry.');
      }
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card style={{ padding: 24, marginTop: 24 }}>
      <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 2, color: C.limeFg, marginBottom: 12 }}>
        ACP · STRIPE CONNECT PROBE
      </div>
      <p style={{ fontFamily: sansFont, fontSize: 14, color: C.text, lineHeight: 1.6, margin: '0 0 8px' }}>
        Runs the one gating check for the Agentic Commerce Protocol settlement design (ADR-0130):
        can a Shared Payment Token be redeemed into a charge under a Connect connected account,
        the way Sill settles? This mints a <strong>test-mode</strong> token and tries it — no real
        money moves, and no card or secret is ever shown.
      </p>
      <p style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, lineHeight: 1.6, margin: '0 0 20px' }}>
        Superadmin-only. Uses the platform Stripe test key and the configured test connected
        account. If the token preview isn’t enabled on the Stripe test account, the result will
        say so.
      </p>

      <button
        type="button"
        onClick={() => void run()}
        disabled={running}
        className="px-4 py-2"
        style={{
          fontFamily: sansFont,
          fontWeight: 600,
          fontSize: 13,
          background: running ? C.surface2 ?? C.surface : C.lime,
          color: running ? C.muted : C.ink,
          borderRadius: 2,
          opacity: running ? 0.7 : 1,
        }}
      >
        {running ? 'Running probe…' : 'Run probe'}
      </button>

      {error !== null ? (
        <p style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 16, marginBottom: 0 }}>
          {error}
        </p>
      ) : null}

      {result !== null ? <AcpProbeResultBlock result={result} /> : null}
    </Card>
  );
}

/** Colour + label for each verdict. */
function verdictStyle(
  C: ReturnType<typeof useC>,
  verdict: AdminAcpProbeVerdict,
): { color: string; label: string } {
  switch (verdict) {
    case 'GO':
      return { color: C.limeFg, label: 'GO' };
    case 'NO_GO_RECOVERABLE':
      return { color: C.amberFg ?? C.muted, label: 'NO-GO · RECOVERABLE' };
    case 'HARD_NO':
      return { color: C.coralFg, label: 'HARD NO' };
    case 'SETUP_ERROR':
      return { color: C.amberFg ?? C.muted, label: 'SETUP ERROR' };
    case 'CONFIG_ERROR':
      return { color: C.amberFg ?? C.muted, label: 'CONFIG ERROR' };
  }
}

function AcpProbeResultBlock({ result }: { result: AdminAcpProbeResult }): JSX.Element {
  const C = useC();
  const v = verdictStyle(C, result.verdict);
  const rowStyle = { fontFamily: monoFont, fontSize: 11, color: C.muted, margin: '2px 0' } as const;

  return (
    <div
      style={{
        marginTop: 20,
        paddingTop: 20,
        borderTop: `1px solid ${C.border}`,
      }}
    >
      <div className="flex items-center gap-3" style={{ marginBottom: 12 }}>
        <span
          style={{
            fontFamily: monoFont,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 1,
            color: C.ink,
            background: v.color,
            padding: '4px 10px',
            borderRadius: 2,
          }}
        >
          {v.label}
        </span>
        <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
          {result.amount} minor · {result.api_version}
        </span>
      </div>
      <p style={{ fontFamily: sansFont, fontSize: 13, color: C.text, lineHeight: 1.6, margin: '0 0 12px' }}>
        {result.summary}
      </p>
      {result.verdict === 'CONFIG_ERROR' ? (
        <p style={{ fontFamily: sansFont, fontSize: 12.5, color: C.body, lineHeight: 1.6, margin: '0 0 12px' }}>
          This is a setup step, not a failure. The probe needs a Stripe <strong>test-mode</strong> connected
          account to charge against. Set it once on the API, then run the probe again:
          <code style={{ display: 'block', fontFamily: monoFont, fontSize: 11, marginTop: 6, overflowWrap: 'anywhere' }}>
            fly secrets set ACP_PROBE_CONNECTED_ACCOUNT=acct_… --app sill-api
          </code>
          Use the acct_… id of any connected account in Stripe test mode (Stripe Dashboard → Connect →
          Connected accounts, with “Test mode” on). This probe returned GO on 2026-06-20 (ADR-0130), so
          it is only needed to re-check that result.
        </p>
      ) : null}
      <div style={{ background: C.surface2 ?? C.surface, border: `1px solid ${C.border}`, borderRadius: 2, padding: 12 }}>
        <p style={rowStyle}>connected_account: {result.connected_account || '(none configured)'}</p>
        <p style={rowStyle}>mint_ok: {String(result.mint_ok)}</p>
        {result.model_a !== null ? (
          <p style={rowStyle}>
            model_a (connected-account header): HTTP {result.model_a.http_status}
            {result.model_a.payment_intent_status !== null ? ` · ${result.model_a.payment_intent_status}` : ''}
            {result.model_a.error_code !== null ? ` · ${result.model_a.error_code}` : ''}
          </p>
        ) : null}
        {result.model_b !== null ? (
          <p style={rowStyle}>
            model_b (platform + transfer): HTTP {result.model_b.http_status}
            {result.model_b.payment_intent_status !== null ? ` · ${result.model_b.payment_intent_status}` : ''}
            {result.model_b.error_code !== null ? ` · ${result.model_b.error_code}` : ''}
          </p>
        ) : null}
      </div>
    </div>
  );
}
